/**
 * Stage Chatbot-2 Phase 2:知識ベース管理 Tab。
 *
 * 设计纪律(沿袭 ChatbotSettingsPage):
 * - 走真实 /api/chatbot/knowledge/* API,绝无 mock fallback
 * - 4 状态机:loading / ready / mutating / error
 * - 失败显示 error + 重新加载,不 fallback mock 数据
 * - 空数据 → Empty State
 *
 * 不做(plan v2):
 * - 编辑 doc 内容(只能 delete + 重建)
 * - 批量操作
 * - 全文搜索
 */

import { useEffect, useRef, useState } from "react";
import {
  RefreshCw,
  Trash2,
  Power,
  Plus,
  AlertCircle,
  FileText,
  Download,
  CheckCircle2,
  XCircle,
  Loader2,
  Search,
  MessageSquare,
  Square,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import {
  type KnowledgeDocument,
  listKnowledge,
  reindexKnowledge,
  patchKnowledge,
  deleteKnowledge,
  createKnowledge,
  importWpPosts,
  importWpPages,
  searchKnowledge,
  answerStream,
  bulkKnowledge,
  getKnowledge,
  type SearchHit,
  type AnswerStreamEvent,
  type KnowledgeBulkAction,
} from "./api/chatbotApi";
import { KnowledgeViewModal } from "./KnowledgeViewModal";
import { KnowledgeCreateModal } from "./KnowledgeCreateModal";

// ============================================================
// State machine
// ============================================================

type State =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; docs: KnowledgeDocument[]; mutating: boolean; flash: string | null };

const FLASH_DURATION_MS = 2500;

// ============================================================
// Phase 2.5:検索テスト専用状態(独立 state,knowledge list と疎結合)
// ============================================================

type SearchState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "ready"; hits: SearchHit[] };

// 検索対象 Top-K(backend _MAX_TOP_K = 20 に揃える)
const SEARCH_TOP_K = 5;

// ============================================================
// Phase 3:回答テスト(RAG)専用 state
//   与 search state 独立;Phase 3 不复用 search 的 textarea / 按钮
// ============================================================

type AnswerState =
  | { kind: "idle" }
  | { kind: "searching"; question: string }       // 检索阶段(读 phase 2.5 service)
  | { kind: "streaming"; question: string; hits: SearchHit[]; partial: string }
  | {
      kind: "done";
      question: string;
      hits: SearchHit[];
      final: string;
      fallback: boolean;
    }
  | { kind: "error"; question: string; message: string };

// 流式渲染:每 ~50ms batch 一次 setState — 不让每个 delta 击一次 render
const STREAM_FLUSH_MS = 50;

// ============================================================
// Status badge
// ============================================================

function StatusBadge({ status }: { status: KnowledgeDocument["status"] }) {
  const config: Record<
    KnowledgeDocument["status"],
    { label: string; bg: string; border: string; color: string; icon: React.ReactNode }
  > = {
    pending: {
      label: "未処理",
      bg: "#fef3c7",
      border: "#fde68a",
      color: "#92400e",
      icon: <Loader2 size={12} />,
    },
    processing: {
      label: "処理中",
      bg: "#dbeafe",
      border: "#bfdbfe",
      color: "#1e40af",
      icon: <Loader2 size={12} className="spin" />,
    },
    ready: {
      label: "準備完了",
      bg: "#dcfce7",
      border: "#bbf7d0",
      color: "#15803d",
      icon: <CheckCircle2 size={12} />,
    },
    failed: {
      label: "失敗",
      bg: "#fee2e2",
      border: "#fecaca",
      color: "#991b1b",
      icon: <XCircle size={12} />,
    },
    disabled: {
      label: "無効",
      bg: "#f3f4f6",
      border: "#e5e7eb",
      color: "#6b7280",
      icon: <Power size={12} />,
    },
    unindexed: {
      label: "未索引",
      bg: "#f1f5f9",
      border: "#cbd5e1",
      color: "#475569",
      icon: <Square size={12} />,
    },
  };
  const c = config[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "2px 8px",
        borderRadius: 6,
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {c.icon}
      {c.label}
    </span>
  );
}

// ============================================================
// Source type label
// ============================================================

function sourceTypeLabel(t: KnowledgeDocument["source_type"]): string {
  switch (t) {
    case "wp_post":
      return "WP 記事";
    case "wp_page":
      return "WP 固定ページ";
    case "manual_text":
      return "手動入力";
  }
}

// ============================================================
// Main
// ============================================================

export const ChatbotKnowledgeTab = () => {
  const [state, setState] = useState<State>({ kind: "loading" });

  // Stage Chatbot-2.5:検索テスト専用 state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchState, setSearchState] = useState<SearchState>({ kind: "idle" });
  const [searchError, setSearchError] = useState<string | null>(null);

  // Stage Chatbot-3:回答テスト(RAG)専用 state + 流式 batch render
  const [answerQuery, setAnswerQuery] = useState("");
  const [answerState, setAnswerState] = useState<AnswerState>({ kind: "idle" });
  const [answerError, setAnswerError] = useState<string | null>(null);
  const answerCancelRef = useRef<AbortController | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  // 知识库 View Modal:点击标题打开,只读查看
  const [viewDoc, setViewDoc] = useState<KnowledgeDocument | null>(null);
  const [viewContent, setViewContent] = useState<string | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState<string | null>(null);

  // 知识库 Create Modal:テキスト追加按钮打开
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = async () => {
    setState({ kind: "loading" });
    try {
      const res = await listKnowledge();
      setState({ kind: "ready", docs: res.items, mutating: false, flash: null });
      setSelectedIds((prev) => prev.filter((id) => res.items.some((doc) => doc.id === id)));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "読み込みに失敗しました。";
      setState({ kind: "error", message: msg });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setMutating = (mutating: boolean) =>
    setState((prev) => (prev.kind === "ready" ? { ...prev, mutating } : prev));

  const setFlash = (msg: string) => {
    setState((prev) =>
      prev.kind === "ready" ? { ...prev, flash: msg } : prev,
    );
    setTimeout(() => {
      setState((prev) =>
        prev.kind === "ready" ? { ...prev, flash: null } : prev,
      );
    }, FLASH_DURATION_MS);
  };

  // ----------------------------------------------------------
  // Actions
  // ----------------------------------------------------------

  const handleReindex = async (doc: KnowledgeDocument) => {
    setMutating(true);
    try {
      await reindexKnowledge(doc.id);
      setFlash(`${doc.title} を再インデックスしました`);
      await load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "再インデックスに失敗しました。");
      setMutating(false);
    }
  };

  const handleToggleEnabled = async (doc: KnowledgeDocument) => {
    setMutating(true);
    try {
      const enabled = doc.status === "disabled";
      await patchKnowledge(doc.id, { enabled });
      setFlash(enabled ? `${doc.title} を有効化しました` : `${doc.title} を無効化しました`);
      await load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "操作に失敗しました。");
      setMutating(false);
    }
  };

  const handleDelete = async (doc: KnowledgeDocument) => {
    if (!window.confirm(`「${doc.title}」を削除しますか?\n関連するチャンクもすべて削除されます。`)) {
      return;
    }
    setMutating(true);
    try {
      await deleteKnowledge(doc.id);
      setFlash(`${doc.title} を削除しました`);
      await load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "削除に失敗しました。");
      setMutating(false);
    }
  };

  const handleImport = async (kind: "posts" | "pages") => {
    setMutating(true);
    try {
      const result = kind === "posts" ? await importWpPosts() : await importWpPages();
      setFlash(
        `インポート完了: ${result.created} 件追加 / ${result.skipped} 件スキップ / 全 ${result.total_seen} 件`,
      );
      await load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "インポートに失敗しました。");
      setMutating(false);
    }
  };

  const handleOpenCreate = () => {
    setCreateError(null);
    setCreateOpen(true);
  };

  const handleCloseCreate = () => {
    if (createBusy) return;
    setCreateOpen(false);
    setCreateError(null);
  };

  const handleCreateManual = async (title: string, content: string) => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const created = await createKnowledge({ title, content });
      setFlash(`${created.title} を追加しました(再インデックスで有効化)`);
      setCreateOpen(false);
      await load();
    } catch (err) {
      // 留在 modal 内显示错误
      setCreateError(err instanceof Error ? err.message : "作成に失敗しました。");
    } finally {
      setCreateBusy(false);
    }
  };

  // 点击标题 → 打开 View Modal;直接调用 getKnowledge 取得 content
  const handleView = async (doc: KnowledgeDocument) => {
    setViewDoc(doc);
    setViewContent(null);
    setViewError(null);
    setViewLoading(true);
    try {
      const detail = await getKnowledge(doc.id);
      // 优先用 detail.content;若不存在,列表项的 content(若 list 也带)
      const c = detail.content ?? doc.content ?? null;
      setViewContent(c);
    } catch (err) {
      setViewError(err instanceof Error ? err.message : "本文の読み込みに失敗しました。");
    } finally {
      setViewLoading(false);
    }
  };

  const handleCloseView = () => {
    setViewDoc(null);
    setViewContent(null);
    setViewError(null);
    setViewLoading(false);
  };

  const handleBulk = async (action: KnowledgeBulkAction) => {
    if (!selectedIds.length) return;
    if (action === "delete" && !window.confirm(`${selectedIds.length} 件を削除しますか?\n関連するチャンクもすべて削除されます。`)) {
      return;
    }
    setMutating(true);
    try {
      const result = await bulkKnowledge(selectedIds, action);
      setFlash(`一括処理完了: ${result.succeeded} 件成功${result.failed ? ` / ${result.failed} 件失敗` : ""}`);
      if (action === "delete") setSelectedIds([]);
      await load();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "一括処理に失敗しました。");
      setMutating(false);
    }
  };

  // ----------------------------------------------------------
  // Stage Chatbot-2.5:検索テスト handler
  // ----------------------------------------------------------
  const handleSearch = async () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchError(null);
    setSearchState({ kind: "searching" });
    try {
      const res = await searchKnowledge(q, SEARCH_TOP_K);
      setSearchState({ kind: "ready", hits: res.results });
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "検索に失敗しました。";
      setSearchError(msg);
      setSearchState({ kind: "idle" });
    }
  };

  // ----------------------------------------------------------
  // Stage Chatbot-3:回答テスト(RAG)handler — SSE 流式 + 停止
  // ----------------------------------------------------------
  const handleAnswerStart = async () => {
    const q = answerQuery.trim();
    if (!q) return;
    setAnswerError(null);

    // 先切到 searching — backend 端 Phase 2.5 retrieval 在 stream 之前的
    // 抢检。candidates event 后会切到 streaming。
    setAnswerState({ kind: "searching", question: q });

    const ac = new AbortController();
    answerCancelRef.current = ac;

    // 流式 batch render — let partialTextRef 累积,不每次 setState
    let partialText = "";
    let pendingTimer: number | null = null;
    let streamedHits: SearchHit[] = [];
    const flush = () => {
      setAnswerState((prev) =>
        prev.kind === "streaming"
          ? { ...prev, partial: partialText }
          : prev,
      );
      pendingTimer = null;
    };

    try {
      const iter = answerStream(q, SEARCH_TOP_K, "standard_qa", ac.signal);
      for await (const ev of iter) {
        if (ac.signal.aborted) {
          // user pressed Stop — silently exit; handleAnswerStop already set idle
          break;
        }

        if (ev.kind === "candidates") {
          streamedHits = ev.hits;
          setAnswerState({
            kind: "streaming",
            question: q,
            hits: ev.hits,
            partial: "",
          });
        } else if (ev.kind === "delta") {
          partialText += ev.text;
          if (pendingTimer === null) {
            pendingTimer = window.setTimeout(flush, STREAM_FLUSH_MS);
          }
        } else if (ev.kind === "done") {
          // 最后一批 partialize — 刷关 timer
          if (pendingTimer !== null) {
            window.clearTimeout(pendingTimer);
            flush();
          }
          setAnswerState({
            kind: "done",
            question: q,
            hits: streamedHits,
            final: ev.final_text,
            fallback: ev.fallback,
          });
          break;
        } else if (ev.kind === "error") {
          setAnswerError(ev.message);
          setAnswerState({
            kind: "error",
            question: q,
            message: ev.message,
          });
          break;
        }
      }
    } catch (err) {
      // AbortError = user pressed 停止;不显示 error,reset 即可
      if (ac.signal.aborted) {
        // already reset by handleAnswerStop
      } else {
        const msg =
          err instanceof Error ? err.message : "回答生成に失敗しました。";
        setAnswerError(msg);
        setAnswerState({ kind: "error", question: q, message: msg });
      }
    } finally {
      answerCancelRef.current = null;
    }
  };

  const handleAnswerStop = () => {
    // 仅前端掐 reader — backend SSE 仍走完,但客户端拿到 cancel 即 reset UI
    // per plan §5.2 (Recommended) — 用 AbortController 让 fetch 中止
    if (answerCancelRef.current) {
      answerCancelRef.current.abort();
    }
    setAnswerState({ kind: "idle" });
  };

  // ----------------------------------------------------------
  // Render
  // ----------------------------------------------------------

  if (state.kind === "loading") {
    return (
      <Card title="知識ベース">
        <div style={{ padding: 16, color: "#9ca3af", fontSize: 13 }}>
          読み込み中...
        </div>
      </Card>
    );
  }

  if (state.kind === "error") {
    return (
      <Card title="知識ベースを読み込めません">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: 14,
            background: "#fee2e2",
            border: "1px solid #fecaca",
            borderRadius: 8,
            color: "#991b1b",
            marginBottom: 12,
          }}
        >
          <AlertCircle size={16} />
          <span style={{ fontSize: 13 }}>{state.message}</span>
        </div>
        <Button onClick={load}>再読み込み</Button>
      </Card>
    );
  }

  const { docs, mutating, flash } = state;
  const allSelected = docs.length > 0 && docs.every((doc) => selectedIds.includes(doc.id));
  const selectedCount = selectedIds.length;

  return (
    <>
    <Card
      title="知識ベース"
      subtitle="Bot の回答根拠となるドキュメントを管理します"
      actions={
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            onClick={load}
            disabled={mutating}
            iconRight={<RefreshCw size={14} />}
          >
            更新
          </Button>
        </div>
      }
    >
      {/* Import + create actions */}
      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          padding: "0 0 14px 0",
          borderBottom: "1px solid #e5e7eb",
          marginBottom: 14,
        }}
      >
        <Button
          onClick={() => handleImport("posts")}
          disabled={mutating}
          iconRight={<Download size={14} />}
        >
          WP 記事一括インポート
        </Button>
        <Button
          onClick={() => handleImport("pages")}
          disabled={mutating}
          iconRight={<Download size={14} />}
        >
          WP 固定ページ一括インポート
        </Button>
        <Button
          onClick={handleOpenCreate}
          disabled={mutating}
          variant="secondary"
          iconRight={<Plus size={14} />}
        >
          テキスト追加
        </Button>
      </div>

      {/* Flash */}
      {flash && (
        <div
          style={{
            marginBottom: 12,
            padding: 10,
            borderRadius: 8,
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            color: "#15803d",
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <CheckCircle2 size={14} />
          {flash}
        </div>
      )}

      {/* Table or empty state */}
      {docs.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: "#6b7280",
            fontSize: 13,
          }}
        >
          <FileText
            size={32}
            color="#9ca3af"
            style={{ marginBottom: 8 }}
          />
          <p>まだドキュメントがありません。</p>
          <p style={{ marginTop: 4 }}>
            WP から一括インポート、または「テキスト追加」から手動で登録してください。
          </p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          {selectedCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                marginBottom: 10,
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #dbeafe",
                background: "#eff6ff",
                color: "#1e3a8a",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <span>{selectedCount} 件選択中</span>
              <Button variant="secondary" disabled={mutating} onClick={() => handleBulk("reindex")}>再インデックス</Button>
              <Button variant="secondary" disabled={mutating} onClick={() => handleBulk("clear_index")}>Embedding削除</Button>
              <Button variant="secondary" disabled={mutating} onClick={() => handleBulk("enable")}>有効化</Button>
              <Button variant="secondary" disabled={mutating} onClick={() => handleBulk("disable")}>無効化</Button>
              <Button variant="danger" disabled={mutating} onClick={() => handleBulk("delete")}>削除</Button>
              <Button variant="ghost" disabled={mutating} onClick={() => setSelectedIds([])}>選択解除</Button>
            </div>
          )}
          <table className="table" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 34, textAlign: "left" }}>
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={mutating}
                    onChange={(e) => setSelectedIds(e.target.checked ? docs.map((doc) => doc.id) : [])}
                    aria-label="すべて選択"
                  />
                </th>
                <th style={{ textAlign: "left" }}>タイトル</th>
                <th style={{ textAlign: "left" }}>種別</th>
                <th style={{ textAlign: "left" }}>状態</th>
                <th style={{ textAlign: "right" }}>チャンク</th>
                <th style={{ textAlign: "left" }}>更新日時</th>
                <th style={{ textAlign: "right" }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(d.id)}
                      disabled={mutating}
                      onChange={(e) =>
                        setSelectedIds((prev) =>
                          e.target.checked ? [...prev, d.id] : prev.filter((id) => id !== d.id),
                        )
                      }
                      aria-label={`${d.title} を選択`}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      onClick={() => handleView(d)}
                      disabled={mutating}
                      title="本文を表示"
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                        textAlign: "left",
                        fontWeight: 600,
                        fontSize: 13,
                        color: "#1d4ed8",
                        textDecoration: "underline",
                      }}
                    >
                      {d.title}
                    </button>
                    {d.last_error && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#991b1b",
                          marginTop: 2,
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={d.last_error}
                      >
                        {d.last_error}
                      </div>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: "#6b7280" }}>
                    {sourceTypeLabel(d.source_type)}
                  </td>
                  <td>
                    <StatusBadge status={d.status} />
                  </td>
                  <td style={{ textAlign: "right", fontSize: 13 }}>
                    {d.chunk_count ?? "—"}
                  </td>
                  <td style={{ fontSize: 12, color: "#6b7280" }}>
                    {d.updated_at
                      ? new Date(d.updated_at).toLocaleString("ja-JP", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <div
                      style={{
                        display: "inline-flex",
                        gap: 6,
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        className="btn-icon"
                        onClick={() => handleReindex(d)}
                        disabled={mutating || d.status === "processing"}
                        title="再インデックス"
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleToggleEnabled(d)}
                        disabled={mutating || d.status === "processing"}
                        title={d.status === "disabled" ? "有効化" : "無効化"}
                      >
                        <Power size={14} />
                      </button>
                      <button
                        className="btn-icon"
                        onClick={() => handleDelete(d)}
                        disabled={mutating || d.status === "processing"}
                        title="削除"
                        style={{ color: "#dc2626" }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p
        className="field-hint"
        style={{ marginTop: 12, color: "#6b7280", fontSize: 12 }}
      >
        インポート / 追加直後は状態 =「未処理」。各行の「再インデックス」を押すと Voyage で embedding を作成します(同期実行)。
      </p>

      {/* ============================================================== */}
      {/* Stage Chatbot-2.5:検索テスト(Voyage + pgvector) */}
      {/* admin 専用。Phase 3 が同じ service を LLM に feeding する前段。 */}
      {/* ============================================================== */}
      <div
        style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <h3
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 6,
          }}
        >
          検索テスト(Voyage + pgvector)
        </h3>
        <p
          className="field-hint"
          style={{ marginBottom: 12, color: "#6b7280" }}
        >
          管理者が質問を投げて、準備完了のチャンクだけが pgvector cosine で返るかを検証します。LLM はまだ繋いでいません — ヒットだけ表示されます。
        </p>

        <div className="field">
          <label className="field-label">質問</label>
          <textarea
            className="textarea"
            style={{ minHeight: 70 }}
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setSearchError(null);
            }}
            placeholder="例:Businessプランの料金はいくらですか?"
            maxLength={1000}
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
            }}
          >
            <span style={{ fontSize: 11, color: "#9ca3af" }}>
              {searchQuery.length} / 1000
            </span>
            <Button
              onClick={handleSearch}
              disabled={
                !searchQuery.trim() || searchState.kind === "searching"
              }
              iconRight={<Search size={14} />}
            >
              {searchState.kind === "searching" ? "検索中…" : "検索テスト"}
            </Button>
          </div>
        </div>

        {/* Error inline */}
        {searchError && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 8,
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 13,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <AlertCircle size={14} />
            {searchError}
          </div>
        )}

        {/* Searching */}
        {searchState.kind === "searching" && (
          <div
            style={{
              marginTop: 12,
              padding: 20,
              textAlign: "center",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            <Loader2
              size={20}
              color="#9ca3af"
              className="spin"
              style={{ marginBottom: 4 }}
            />
            <div>クエリを埋め込み、検索しています…</div>
          </div>
        )}

        {/* Results */}
        {searchState.kind === "ready" &&
          searchState.hits.length === 0 && (
            <div
              style={{
                marginTop: 12,
                padding: 18,
                textAlign: "center",
                color: "#6b7280",
                fontSize: 13,
                background: "#f9fafb",
                borderRadius: 8,
                border: "1px dashed #e5e7eb",
              }}
            >
              該当するチャンクがありません。インデックス化が完了していないか、ドキュメントが登録されていません。
            </div>
          )}

        {searchState.kind === "ready" && searchState.hits.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginBottom: 6,
                fontWeight: 600,
              }}
            >
              {searchState.hits.length} 件のヒット
            </div>
            <table
              className="table"
              style={{
                width: "100%",
                background: "#f9fafb",
                borderRadius: 8,
              }}
            >
              <thead>
                <tr style={{ background: "#f3f4f6" }}>
                  <th style={{ textAlign: "left" }}>タイトル</th>
                  <th style={{ textAlign: "left" }}>種別</th>
                  <th style={{ textAlign: "right" }}>#</th>
                  <th style={{ textAlign: "right" }}>スコア</th>
                  <th style={{ textAlign: "left" }}>プレビュー</th>
                </tr>
              </thead>
              <tbody>
                {searchState.hits.map((h, i) => (
                  <tr key={`${h.chunk_id}-${i}`}>
                    <td
                      style={{ fontSize: 13, maxWidth: 240 }}
                      title={h.document_title}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {h.document_title}
                      </div>
                      {h.heading_path && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#2563eb",
                            marginTop: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={h.heading_path}
                        >
                          {h.heading_path}
                        </div>
                      )}
                      {h.source_url && (
                        <div
                          style={{
                            fontSize: 11,
                            color: "#6b7280",
                            marginTop: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={h.source_url}
                        >
                          {h.source_url}
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12, color: "#6b7280" }}>
                      {sourceTypeLabel(h.source_type)}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontSize: 12,
                        color: "#6b7280",
                      }}
                    >
                      {h.chunk_index}
                    </td>
                    <td
                      style={{
                        textAlign: "right",
                        fontSize: 13,
                        fontWeight: 700,
                        color: h.score >= 0.7 ? "#15803d" : h.score >= 0.5 ? "#92400e" : "#6b7280",
                      }}
                    >
                      {h.score.toFixed(2)}
                    </td>
                    <td
                      style={{
                        fontSize: 12,
                        color: "#374151",
                        maxWidth: 360,
                      }}
                      title={h.content_preview}
                    >
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {h.content_preview}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ============================================================== */}
      {/* Stage Chatbot-3 Phase 3:回答テスト(RAG)— SSE 流式 */}
      {/* 检索 → prompt → AI Engine → 流式 delta + 使用した検索候補 */}
      {/* admin 専用。Public chat / SSE 是 Phase 4。 */}
      {/* ============================================================== */}
      <div
        style={{
          marginTop: 24,
          paddingTop: 18,
          borderTop: "1px solid #e5e7eb",
        }}
      >
        <h3
          style={{
            fontSize: 14,
            fontWeight: 700,
            color: "#111827",
            marginBottom: 6,
          }}
        >
          回答テスト(RAG)
        </h3>
        <p
          className="field-hint"
          style={{ marginBottom: 12, color: "#6b7280" }}
        >
          質問を投げて、Retrieval 結果のみを根拠に AI が答えます(管理画面専用)。
          検索でヒットなしの場合は LLM を呼ばず固定の「資料なし」回答を返します。
        </p>

        <div className="field">
          <label className="field-label">質問</label>
          <textarea
            className="textarea"
            style={{ minHeight: 70 }}
            value={answerQuery}
            onChange={(e) => {
              setAnswerQuery(e.target.value);
              setAnswerError(null);
            }}
            placeholder="例:Businessプランの料金はいくらですか?"
            maxLength={1000}
            disabled={
              answerState.kind === "searching" ||
              answerState.kind === "streaming"
            }
          />
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginTop: 6,
              gap: 8,
            }}
          >
            <span style={{ fontSize: 11, color: "#9ca3af" }}>
              {answerQuery.length} / 1000
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <Button
                onClick={handleAnswerStart}
                disabled={
                  !answerQuery.trim() ||
                  answerState.kind === "searching" ||
                  answerState.kind === "streaming"
                }
                iconRight={<MessageSquare size={14} />}
              >
                {answerState.kind === "searching"
                  ? "検索中…"
                  : answerState.kind === "streaming"
                    ? "生成中…"
                    : "回答テスト"}
              </Button>
              <Button
                onClick={handleAnswerStop}
                disabled={answerState.kind !== "streaming"}
                variant="secondary"
                iconRight={<Square size={14} />}
              >
                停止
              </Button>
            </div>
          </div>
        </div>

        {/* Error inline */}
        {answerError && answerState.kind !== "error" && (
          <div
            style={{
              marginTop: 10,
              padding: 10,
              borderRadius: 8,
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            <AlertCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {answerError}
          </div>
        )}

        {/* 1. searching phase — Retrieval 阶段 */}
        {answerState.kind === "searching" && (
          <div
            style={{
              marginTop: 12,
              padding: 20,
              textAlign: "center",
              color: "#6b7280",
              fontSize: 13,
            }}
          >
            <Loader2
              size={20}
              color="#9ca3af"
              className="spin"
              style={{ marginBottom: 4 }}
            />
            <div>資料を検索しています…</div>
          </div>
        )}

        {/* 2. streaming — partial 流式累积 */}
        {answerState.kind === "streaming" && (
          <div
            style={{
              marginTop: 12,
              padding: 14,
              borderRadius: 8,
              background: "#f0f9ff",
              border: "1px solid #bae6fd",
              fontSize: 14,
              lineHeight: 1.7,
              whiteSpace: "pre-wrap",
              minHeight: 60,
            }}
          >
            {answerState.partial || (
              <span style={{ color: "#9ca3af", fontSize: 13 }}>
                生成待ち…
              </span>
            )}
          </div>
        )}

        {/* 3. done — final + fallback badge + 使用した検索候補 */}
        {answerState.kind === "done" && (
          <>
            <div
              style={{
                marginTop: 12,
                padding: 14,
                borderRadius: 8,
                background: answerState.fallback ? "#fef3c7" : "#f0fdf4",
                border: `1px solid ${answerState.fallback ? "#fde68a" : "#bbf7d0"}`,
                fontSize: 14,
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
              }}
            >
              {answerState.final}
            </div>
            {answerState.fallback && (
              <div
                style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: "#92400e",
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  padding: "4px 10px",
                  borderRadius: 6,
                  display: "inline-block",
                }}
              >
                資料なし・固定回答(LLM は呼ばれていません)
              </div>
            )}

            {answerState.hits.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  使用した検索候補 ({answerState.hits.length} 件)
                </div>
                <table
                  className="table"
                  style={{
                    width: "100%",
                    background: "#f9fafb",
                    borderRadius: 8,
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th style={{ textAlign: "left" }}>タイトル</th>
                      <th style={{ textAlign: "left" }}>種別</th>
                      <th style={{ textAlign: "right" }}>#</th>
                      <th style={{ textAlign: "right" }}>スコア</th>
                    </tr>
                  </thead>
                  <tbody>
                    {answerState.hits.map((h, i) => (
                      <tr key={`${h.chunk_id}-${i}`}>
                        <td
                          style={{ fontSize: 13, maxWidth: 360 }}
                          title={h.document_title}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            [{i + 1}] {h.document_title}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: "#6b7280" }}>
                          {sourceTypeLabel(h.source_type)}
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontSize: 12,
                            color: "#6b7280",
                          }}
                        >
                          {h.chunk_index}
                        </td>
                        <td
                          style={{
                            textAlign: "right",
                            fontSize: 13,
                            fontWeight: 700,
                            color:
                              h.score >= 0.7
                                ? "#15803d"
                                : h.score >= 0.5
                                  ? "#92400e"
                                  : "#6b7280",
                          }}
                        >
                          {(h.score ?? 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* 4. error */}
        {answerState.kind === "error" && (
          <div
            style={{
              marginTop: 12,
              padding: 14,
              borderRadius: 8,
              background: "#fee2e2",
              border: "1px solid #fecaca",
              color: "#991b1b",
              fontSize: 13,
            }}
          >
            <XCircle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
            {answerState.message}
          </div>
        )}
      </div>
    </Card>

    {/* 知识库 View Modal — 点击标题打开,只读查看 */}
    <KnowledgeViewModal
      open={viewDoc !== null}
      doc={viewDoc}
      content={viewContent}
      loading={viewLoading}
      error={viewError}
      onClose={handleCloseView}
    />

    {/* 知识库 Create Modal — テキスト追加 按钮打开 */}
    <KnowledgeCreateModal
      open={createOpen}
      creating={createBusy}
      error={createError}
      onCancel={handleCloseCreate}
      onCreate={handleCreateManual}
    />
    </>
  );
};
